import { atom } from 'jotai';
import { atomWithReset } from 'jotai/utils';
import { atomWithQuery } from 'jotai-tanstack-query';

import { loginCodeAtom } from '_atoms/auth';
import { languagesGetQuery } from '_queries/languagesQueries';

import type { AxiosError, AxiosResponse } from 'axios';
import type { PaginatedResponse } from '@/types/api';
import type { LoginCode } from '@/types/auth';
import type { Language } from '@/types/batch';

/** The ID of the language in which the respondent chooses to complete materials. */
export const languageIdAtom = atomWithReset<string | null>(null);

/** Fetch the list of languages from the API */
export const languagesAtom = atomWithQuery(() => {
  return {
    queryKey: ['languages'],
    queryFn: async (): Promise<Language[]> => {
      try {
        const response: AxiosResponse<PaginatedResponse<Language>> = await languagesGetQuery();
        return response.data.results;
      } catch (e) {
        const error = e as AxiosError<any>;
        const message = error?.response?.data?.message || error?.response?.statusText || error.message;
        throw new Error(message);
      }
    },
    staleTime: Infinity, // never refetch automatically
    gcTime: Infinity,    // never do garbage collection unless explicitly invalidated
  };
});

/** Filter the list of languages to include only the ones available to the respondent (based on their login code). */
export const availableLanguagesAtom = atom<Language[]>((get) => {
  const loginCode: LoginCode = get(loginCodeAtom);
  const languages: Language[] | undefined = get(languagesAtom).data;

  return languages?.filter(( language: Language ) => loginCode?.languages?.includes(language?.id)) || [];
});